import {
  coerceCurrencyForProduct,
  coerceLocaleForProduct,
  getSupportedCurrencies,
  getSupportedLocales,
  type TokenizationPreviewFeatures,
  type TokenizationWidgetConfig,
} from "@blockdaemon/widget-config";
import {
  getColorSchemeOverride,
  getCurrencyOverride,
  getHeightOverride,
  getLocaleOverride,
  getWalletModeOverride,
  getWidthOverride,
  useWidgetConfigAuth,
} from "@blockdaemon/widget-host";
import { SharedWidgetConfigProvider, type SharedWidgetAppearance } from "@blockdaemon/widget-shell";

const TOKENIZATION_SUPPORTED_CURRENCIES = getSupportedCurrencies("tokenization");
const TOKENIZATION_SUPPORTED_LOCALES = getSupportedLocales("tokenization");
import { Center, Loader } from "@mantine/core";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useState } from "react";

import { invalidateTokenizationConfigQueries, useTokenizationConfigQuery } from "../config-loader";

interface TokenizationWidgetConfigContextType {
  config: TokenizationWidgetConfig;
  updateConfig: (updates: Partial<TokenizationWidgetConfig>) => void;
  updateFeatures: (updates: Partial<TokenizationPreviewFeatures>) => void;
  updateAppearance: (updates: Partial<TokenizationWidgetConfig["appearance"]>) => void;
  toggleCoreTokenizationFeature: () => void;
  resetConfig: () => void;
  features: TokenizationWidgetConfig["features"];
  appearance: TokenizationWidgetConfig["appearance"];
  isLoading: boolean;
}

const TokenizationWidgetConfigContext = createContext<
  TokenizationWidgetConfigContextType | undefined
>(undefined);

export function TokenizationWidgetConfigProvider({ children }: { children: ReactNode }) {
  const { serverConfig, isConfigLoading, configQueryError, isAwaitingAuthToken, hasAuthFailure } =
    useWidgetConfigAuth({
      useConfigQuery: useTokenizationConfigQuery,
    });
  const [localConfig, setLocalConfig] = useState<TokenizationWidgetConfig | null>(null);

  const currentConfig = localConfig ?? serverConfig ?? null;

  const urlColorScheme = getColorSchemeOverride();
  const urlWidth = getWidthOverride();
  const urlHeight = getHeightOverride();
  const urlCurrency = getCurrencyOverride(TOKENIZATION_SUPPORTED_CURRENCIES);
  const urlLocale = getLocaleOverride(TOKENIZATION_SUPPORTED_LOCALES);
  const urlWalletMode = getWalletModeOverride();

  const urlMergedConfig = currentConfig
    ? {
        ...currentConfig,
        features: {
          ...currentConfig.features,
          walletMode: urlWalletMode || currentConfig.features.walletMode,
        },
        appearance: {
          ...currentConfig.appearance,
          theme: urlColorScheme ?? currentConfig.appearance.theme,
          width: urlWidth !== null ? urlWidth : currentConfig.appearance.width,
          height: urlHeight !== null ? urlHeight : currentConfig.appearance.height,
          // Coerce invalid customer config (schema allows USD only); URL override still optional.
          currency: coerceCurrencyForProduct(
            "tokenization",
            urlCurrency ?? currentConfig.appearance.currency,
          ),
          locale: coerceLocaleForProduct(
            "tokenization",
            urlLocale ?? currentConfig.appearance.locale,
          ),
        },
      }
    : null;

  const updateConfig = useCallback(
    (updates: Partial<TokenizationWidgetConfig>) => {
      setLocalConfig((prev) => {
        const base = prev ?? serverConfig;
        if (!base) return prev;
        return {
          ...base,
          ...updates,
          features: updates.features ? { ...base.features, ...updates.features } : base.features,
          appearance: updates.appearance
            ? { ...base.appearance, ...updates.appearance }
            : base.appearance,
        };
      });
    },
    [serverConfig],
  );

  const updateFeatures = useCallback(
    (updates: Partial<TokenizationPreviewFeatures>) => {
      setLocalConfig((prev) => {
        const base = prev ?? serverConfig;
        if (!base) return prev;
        return {
          ...base,
          features: { ...base.features, ...updates },
        };
      });
    },
    [serverConfig],
  );

  const updateAppearance = useCallback(
    (updates: Partial<TokenizationWidgetConfig["appearance"]>) => {
      setLocalConfig((prev) => {
        const base = prev ?? serverConfig;
        if (!base) return prev;
        return {
          ...base,
          appearance: { ...base.appearance, ...updates },
        };
      });
    },
    [serverConfig],
  );

  // Toggles the core tokenization feature, but intentionally blocks disabling — once
  // the tokenization feature is on it cannot be switched off in preview mode (the
  // showcase switch is rendered with `disabled` when already enabled).
  const toggleCoreTokenizationFeature = useCallback(() => {
    setLocalConfig((prev) => {
      const base = prev ?? serverConfig;
      if (!base) return prev;
      const proposed: TokenizationPreviewFeatures = {
        ...base.features,
        tokenization: {
          ...base.features.tokenization,
          enabled: !base.features.tokenization.enabled,
        },
      };
      if (!proposed.tokenization.enabled) {
        return prev;
      }
      return { ...base, features: proposed };
    });
  }, [serverConfig]);

  const resetConfig = useCallback(() => {
    setLocalConfig(null);
    invalidateTokenizationConfigQueries();
  }, []);

  if (isConfigLoading || isAwaitingAuthToken) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (hasAuthFailure) {
    return null;
  }

  if (configQueryError) {
    throw new Error(
      configQueryError instanceof Error
        ? configQueryError.message
        : "Failed to load tokenization widget config",
    );
  }

  if (!urlMergedConfig) {
    throw new Error("Tokenization widget config not found");
  }

  // Typed adapter: SharedWidgetAppearance is broader (e.g. locale: string) than
  // the tokenization appearance type. The cast is safe because the shared type
  // only carries fields the tokenization appearance already handles.
  const updateSharedAppearance = (updates: Partial<SharedWidgetAppearance>) => {
    updateAppearance(updates as Partial<TokenizationWidgetConfig["appearance"]>);
  };

  return (
    <TokenizationWidgetConfigContext.Provider
      value={{
        config: urlMergedConfig,
        updateConfig,
        updateFeatures,
        updateAppearance,
        toggleCoreTokenizationFeature,
        resetConfig,
        features: urlMergedConfig.features,
        appearance: urlMergedConfig.appearance,
        isLoading: false,
      }}
    >
      <SharedWidgetConfigProvider
        value={{
          config: urlMergedConfig,
          appearance: urlMergedConfig.appearance,
          updateAppearance: updateSharedAppearance,
          resetConfig,
          isLoading: false,
        }}
      >
        {children}
      </SharedWidgetConfigProvider>
    </TokenizationWidgetConfigContext.Provider>
  );
}

export function useTokenizationWidgetConfig(): TokenizationWidgetConfigContextType {
  const ctx = useContext(TokenizationWidgetConfigContext);
  if (ctx === undefined) {
    throw new Error(
      "useTokenizationWidgetConfig must be used within TokenizationWidgetConfigProvider",
    );
  }
  return ctx;
}
